import type { Client } from '../../../api/client.js';
import { demanderDeblocageMandat, forcerMandat } from '../../genealogie/actions.js';

/**
 * Import de contacts d'un dossier (PARITE-5, démo brigitte/ImportContacts) : deux routes du contrat seulement, l'aperçu
 * (`genealogie.contacts.apercu`, aucune écriture) puis la confirmation (`genealogie.contacts.importer`) du même fichier.
 */
export type IssueLigne = 'a_importer' | 'doublon' | 'erreur';

export interface LigneApercu {
  line: number;
  firstNames: string;
  lastName: string;
  vitalStatus: 'vivant' | 'decede' | 'inconnu';
  role: 'heritier' | 'conjoint' | 'autre';
  phone: string | null;
  email: string | null;
  preferredChannel: 'whatsapp' | 'email' | 'telephone' | 'courrier' | null;
  relationLabel: string | null;
  outcome: IssueLigne;
  reason: string | null;
}

export interface ApercuImport {
  fingerprint: string;
  fileName: string | null;
  separator: ';' | ',';
  totalRows: number;
  importableCount: number;
  duplicateCount: number;
  errorCount: number;
  preview: LigneApercu[];
  duplicates: { line: number; reason: string }[];
  errors: { line: number; reason: string }[];
  columnsRecognized: string[];
  columnsIgnored: string[];
  mandateSigned: boolean;
  /** Forçage du mandat par la dirigeante qui lève le blocage (jamais bloqué, décision de Joel du 30/10) ; null sans forçage. */
  mandateForcing?: string | null;
  limits: { maxRows: number; maxBytes: number; previewRows: number };
}

export interface RapportImport {
  createdCount: number;
  duplicateCount: number;
  errorCount: number;
  persons: { id: string; displayName: string }[];
}

export interface SourceImportContacts {
  apercu(csv: string, fileName: string): Promise<ApercuImport>;
  importer(csv: string, fileName: string, fingerprint: string, expectedCount: number): Promise<RapportImport>;
  /** Forçage tracé du mandat par la dirigeante (route `demarrage.forcer`, portée « mandat_recherche ») ; vrai si enregistré. */
  forcerMandat?(motif: string): Promise<boolean>;
  /** L'assistante prévient la dirigeante (route `demarrage.demanderDeblocage`) ; vrai si la demande est enregistrée. */
  prevenirDirigeante?(): Promise<boolean>;
}

export const PERMISSION_IMPORTER = 'contact:import:dossier';

export function creerSourceImportContacts(client: Client, dossierId: string): SourceImportContacts {
  return {
    apercu: (csv, fileName) =>
      client.appeler<ApercuImport>('genealogie.contacts.apercu', { parametres: { dossierId }, corps: { csv, fileName } }),
    importer: (csv, fileName, fingerprint, expectedCount) =>
      client.appeler<RapportImport>('genealogie.contacts.importer', {
        parametres: { dossierId },
        corps: { csv, fileName, fingerprint, expectedCount },
      }),
    forcerMandat: (motif) => forcerMandat(client, dossierId, motif),
    prevenirDirigeante: () => demanderDeblocageMandat(client, dossierId),
  };
}
